import axios from '@fs/zion-axios'
import { baseUrl } from '@fs/zion-config'
import { getPersonFromFieldId, getRecordPersonRights } from '@fs/zion-sls'
import { SG_BASE_URL, headers } from '../config'

const recordPermissionsCache = {}
/**
 * Checks if the user has permission to see one image.
 * Checks for an apid or ark, but not both at the same time
 * Calls this endpoint: https://www.familysearch.org/developers/docs/api/artifacts/Artifact_Images_Permissions_resource
 * @param {String} apid - e.g. TH-909-59425-124943-79
 * @param {String} ark - e.g. 3:1:S3HY-66TS-F3K
 * @returns {Boolean} hasPermissions
 */
// eslint-disable-next-line import/prefer-default-export
export async function getImagePermissions(apid = null, ark = null) {
  if (!apid && !ark) return false
  const url = `${SG_BASE_URL}/platform/artifacts/images/permissions`
  const combinedHeaders = {
    ...headers,
    accept: 'application/x-gedcomx-v1+json',
    'content-type': 'application/x-gedcomx-v1+json',
  }
  const imageUrl = apid ? `${baseUrl}/pal:/MM9.3.1/${apid}` : `${baseUrl}/ark:/61903/${ark}`
  const body = {
    sourceDescriptions: [
      {
        id: imageUrl,
      },
    ],
  }
  try {
    const response = await axios.post(url, body, { headers: combinedHeaders })
    const imageRights = response?.data.sourceDescriptions.find((source) => source.id === imageUrl)
    const allowed = imageRights?.rights.find((insetInlineEnd) => {
      return insetInlineEnd === 'http://familysearch.org/v1/Allowed'
    })
    return Boolean(allowed)
  } catch (err) {
    return false
  }
}

/**
    We just need one record or person id off of this page to check permissions
    The assumption is that all records or persons on the image have the same permissions
   */
export function getPersonOrRecordArkFromImage(elements, fieldId) {
  const personElement = getPersonFromFieldId(elements, fieldId)
  let recordOrPersonArk = personElement ? personElement.id : null
  // We just need one record or person id off of this page to check permissions
  // The assumption is that all records or persons on the image have the same permissions
  if (!recordOrPersonArk) {
    const recordEl = elements.find((element) => element.elementType === 'RECORD' && /1:2/.test(element.id))
    if (recordEl) {
      recordOrPersonArk = recordEl.id
    } else {
      const personEl = elements.find((element) => element.elementType === 'PERSON' && /1:1/.test(element.id))
      recordOrPersonArk = personEl ? personEl.id : ''
    }
  }
  return recordOrPersonArk
}
/**
 * Get record permissions from person or record on the image.
 * If neither exists, we return false.  They can't go to a record or person url if there are none.
 */
export async function getRecordPermissions(slsElements, fieldId) {
  let personOrRecordArk = fieldId
  if (!/1:[12]/.test(fieldId)) {
    personOrRecordArk = getPersonOrRecordArkFromImage(slsElements, fieldId)
  }
  if (!personOrRecordArk) return false
  const fromCache = recordPermissionsCache[personOrRecordArk]
  if (typeof fromCache === 'boolean') return fromCache
  const recordResult = await getRecordPersonRights(personOrRecordArk, baseUrl)
  recordPermissionsCache[personOrRecordArk] = recordResult
  return recordResult
}
